import React, { useCallback, useMemo } from 'react';

import {
  CloseButton,
  HTMLSnippetModalStyled,
  LearnMoreLink,
  LearnMoreLinkWrapper,
} from './HTMLSnippet.styles';
import { Dispatch } from 'src/components-dummy/types';
import {
  DialogModalHeader,
  DialogModalContent,
  DialogModalFooter,
  Button,
  TypographyType,
  Typography,
  DialogModalTitle,
  DialogModalButtonsContainer,
  TypographyVariant,
  Icon,
  AvailableIcons,
} from 'src/components-dummy';
import { CodeSection, CodeSectionProps } from 'src/components-dummy/CodeSection';
import { HTMLSnippetActions } from './Actions';
import { copyToClipboard } from 'src/utils';
import { learnMoreLink } from 'src/components-bl/EmailMarketing/constants/learn-more-link';
import { parseHtmlSnippet } from 'src/components-bl/EmailMarketing/helpers';

interface HTMLSnippetProps {
  dispatch: Dispatch;
  onCloseModal: () => void;
  navigateToCampaigns: () => void;
  htmlSnippet?: string;
}

export const HTMLSnippet = ({
  dispatch,
  onCloseModal,
  navigateToCampaigns,
  htmlSnippet,
}: HTMLSnippetProps): JSX.Element => {
  const snippetParts: CodeSectionProps['codeParts'] = useMemo(() => {
    return htmlSnippet
      ? (parseHtmlSnippet(htmlSnippet) as CodeSectionProps['codeParts'])
      : [{ text: 'No snippet available', color: 'black' }];
  }, [htmlSnippet]);

  const onCopySnippetClick = useCallback(() => {
    const textToCopy = snippetParts.map(codePart => codePart.text).join('');

    copyToClipboard(textToCopy);

    dispatch(HTMLSnippetActions.notification({ customMessage: 'Snippet copied to clipboard' }));
  }, [dispatch, snippetParts]);

  return (
    <HTMLSnippetModalStyled open onClose={onCloseModal}>
      <>
        <DialogModalHeader>
          <CloseButton onClick={onCloseModal}>
            <Icon name={AvailableIcons.CloseRoundedSmall} />
          </CloseButton>
          <DialogModalTitle>HTML Snippet</DialogModalTitle>
        </DialogModalHeader>
        <DialogModalContent>
          <LearnMoreLinkWrapper>
            <Typography type={TypographyType.Paragraph} variant={TypographyVariant.SmallRegular}>
              Copy this snippet into an HTML block inside your email template. <br />
              It will load your personalized product experience when the email is opened.
            </Typography>
            <LearnMoreLink>
              <a href={learnMoreLink} target='_blank' rel='noreferrer'>
                See how to add this snippet to your email
              </a>
              <Icon name={AvailableIcons.ArrowRec} />
            </LearnMoreLink>
          </LearnMoreLinkWrapper>
          <CodeSection codeParts={snippetParts} enableCopy={false} className='html-snippet' />
        </DialogModalContent>
        <DialogModalFooter>
          <DialogModalButtonsContainer>
            <Button variant='primary' onClick={onCopySnippetClick}>
              Copy to clipboard
            </Button>
            <Button variant='secondary' onClick={navigateToCampaigns}>
              Close
            </Button>
          </DialogModalButtonsContainer>
        </DialogModalFooter>
      </>
    </HTMLSnippetModalStyled>
  );
};
